import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogContent, DialogTitle, Box, Button, Grid, TextField, Autocomplete} from 'views/components';
import {Translate, translate, DateTimePicker} from 'views/module';

import moment from 'moment-timezone';

import {getEventTimezoneAbbr} from '../dateTime';

const RescheduleEvent = (props) => {
  const {closeModel, event, onRescheduleEvent} = props;

  const [startTime, setStartTime] = React.useState(moment.tz(event.eventData.startTime, event.eventData.timezone));
  const [endTime, setEndTime] = React.useState(moment.tz(event.eventData.endTime, event.eventData.timezone));
  const [timezone, setTimezone] = React.useState(event.eventData.timezone);
  const [reason, setReason] = React.useState('');

  const [isSaving, setIsSaving] = React.useState(false);
  const startTimezoneAbbr = getEventTimezoneAbbr(startTime, timezone);
  const endTimezoneAbbr = getEventTimezoneAbbr(endTime, timezone);

  const makeTimezonesList = React.useCallback(() => {
    const timezones = moment.tz.names();
    return timezones.map((tz) => ({
      group: translate('client.ClientDetail.Scheduling.Timezone.Timezones'),
      value: tz,
      label: tz,
    }));
  }, []);

  const onSave = async (e) => {
    setIsSaving(true);
    const reasonForException = {
      description: {
        blob: reason,
        editorType: 'MARKDOWN',
      },
    };
    const eventData = {
      startTime: moment.tz(moment(startTime).format('YYYY-MM-DDTHH:mm:ss'), timezone).format(),
      endTime: moment.tz(moment(endTime).format('YYYY-MM-DDTHH:mm:ss'), timezone).format(),
      timezone: timezone,
    };
    await onRescheduleEvent(
        event.clientData.clientRn,
        event.eventRn,
        reasonForException,
        eventData,
        () => {
          closeModel(true);
          setIsSaving(false);
        },
    );
  };

  const onClose = () => {
    closeModel(false);
  };

  return (
    <Dialog onClose={onClose} open={true} maxWidth="xs" fullWidth>
      <DialogTitle onClose={onClose}>
        <Translate expression='schedule.eventDetail.RescheduleDialogTitle.Title'/>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} alignItems="flex-start" sx={{mt: 0}}>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={`${translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.StartTime')} (${startTimezoneAbbr})`}
              value={startTime}
              onChange={setStartTime}
              textFieldProps={{fullWidth: true, InputLabelProps: {shrink: true}, helperText: timezone, sx: {mt: 0}}}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={`${translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.EndTime')} (${endTimezoneAbbr})`}
              value={endTime}
              onChange={setEndTime}
              textFieldProps={{fullWidth: true, InputLabelProps: {shrink: true}, helperText: timezone, sx: {mt: 0}}}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={makeTimezonesList()}
              value={timezone}
              label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.Timezone')}
              onChange={(e) => setTimezone(e.value)}
              sx={{minWidth: {xs: '100%', md: '256px'}}}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>{option.label}</li>
              )}
              groupBy={(dropdownItem) => dropdownItem.group}
              isOptionEqualToValue={(option, value) => option.value === value}
              disableClearable
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              placeholder={translate('schedule.eventDetail.Reason.ReasonForReschedule')}
              label={translate('schedule.eventDetail.Reason.Reason')}
              value={reason}
              onChange={(e) => setReason(e)}
              fullWidth
              multiline
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2}}>
        <Button variant="outlined" onClick={onClose}>
          <Translate expression="generic.Cancel" />
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving || (endTime && startTime && endTime.isBefore(startTime))}
        >
          <Translate expression="generic.Save" />
        </Button>
      </Box>
    </Dialog>
  );
};

RescheduleEvent.propTypes = {
  closeModel: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
  onRescheduleEvent: PropTypes.func.isRequired,
};

export default RescheduleEvent;
